import { Box, Stack, Text, Title, Badge, Group } from "@mantine/core";
import { useOutletContext } from "react-router-dom";
import type { PrivateContext } from "@/components/layout/PrivateLayout";
import { releases } from "@/content/releases";
import { hasScope } from "@/lib/auth";
import { AudioPlayer } from "@/components/audio/AudioPlayer";
import { EmptyState } from "@/components/common/EmptyState";
import type { Track, TrackReleaseStatus } from "@/content/types";

const STATUS_LABEL: Record<TrackReleaseStatus, { label: string; color: string }> = {
  released: { label: "Released", color: "gray" },
  forthcoming: { label: "Forthcoming", color: "magenta" },
  "unsigned-finished": { label: "Unsigned", color: "indigo" },
  demo: { label: "Demo / WIP", color: "cyan" },
  "dj-tool": { label: "DJ tool", color: "yellow" },
};

export default function PrivateVault() {
  const { session } = useOutletContext<PrivateContext>();

  if (!hasScope(session, "catalog-unreleased")) {
    return (
      <EmptyState
        title="Out of scope"
        body="Your token doesn't include `catalog-unreleased`."
      />
    );
  }

  type TrackEntry = { track: Track; releaseTitle: string; releaseSlug: string };
  const tracks: TrackEntry[] = releases
    .flatMap((r) =>
      r.tracks
        .filter((t) => t.releaseStatus === "unsigned-finished" || t.releaseStatus === "demo" || t.releaseStatus === "forthcoming")
        .map((t) => ({ track: t, releaseTitle: r.title, releaseSlug: r.slug })),
    );

  const order: TrackReleaseStatus[] = ["forthcoming", "unsigned-finished", "demo"];
  const sorted = tracks.slice().sort(
    (a, b) =>
      order.indexOf(a.track.releaseStatus as TrackReleaseStatus) -
      order.indexOf(b.track.releaseStatus as TrackReleaseStatus),
  );

  if (sorted.length === 0) {
    return <EmptyState title="Vault is empty" />;
  }

  return (
    <Stack gap="lg">
      <Box>
        <Title order={1}>Vault</Title>
        <Text c="dimmed" mt="xs">
          Broader catalog — unsigned-finished + demo material. Status badge tells you the pitch
          readiness.
        </Text>
      </Box>

      <Stack gap="md">
        {sorted.map(({ track, releaseTitle, releaseSlug }) => {
          const status = STATUS_LABEL[track.releaseStatus];
          return (
            <Box
              key={`${releaseSlug}-${track.position}`}
              p="md"
              style={{
                background: "var(--color-deep)",
                border: "1px solid var(--color-ocean)",
                borderRadius: 12,
              }}
            >
              <Group justify="space-between" mb="xs">
                <Text fw={600}>{releaseTitle}</Text>
                <Badge color={status.color} variant="light">
                  {status.label}
                </Badge>
              </Group>
              <AudioPlayer audio={track.master} session={session} />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
